import React, { useState } from 'react';
import {
  LogicRule,
  LogicRuleGroup,
  LogicCondition,
  LogicAction,
  LogicOperator,
  QuestionDefinition,
} from '../../../types/survey.types';

interface RuleBuilderProps {
  rule?: LogicRule;
  currentQuestion: QuestionDefinition;
  availableQuestions: QuestionDefinition[];
  allQuestions: QuestionDefinition[];
  onSave: (rule: LogicRule) => void;
  onCancel: () => void;
}

const RuleBuilder: React.FC<RuleBuilderProps> = ({
  rule,
  currentQuestion,
  availableQuestions,
  allQuestions,
  onSave,
  onCancel,
}) => {
  // Auto-generate rule name
  const generateRuleName = () => {
    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return `Logic Rule - ${timestamp}`;
  };

  const [ruleName] = useState(rule?.name || generateRuleName());
  const [ruleGroups, setRuleGroups] = useState<LogicRuleGroup[]>(
    rule?.ruleGroups || [
      {
        id: `group-${Date.now()}`,
        operator: 'AND',
        conditions: [
          {
            id: `condition-${Date.now()}`,
            questionId: '',
            operator: 'equals',
            value: '',
          },
        ],
      },
    ]
  );
  const [groupOperator, setGroupOperator] = useState<'AND' | 'OR'>(
    rule?.groupOperator || 'OR'
  );
  const [actions, setActions] = useState<LogicAction[]>(
    rule?.actions || [{ type: 'show', targetQuestionId: currentQuestion.id }]
  );

  const operatorOptions: { value: LogicOperator; label: string }[] = [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: '≠' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: '∌' },
    { value: 'greater_than', label: '>' },
    { value: 'less_than', label: '<' },
    { value: 'greater_or_equal', label: '≥' },
    { value: 'less_or_equal', label: '≤' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ];

  const addCondition = (groupId: string) => {
    setRuleGroups(
      ruleGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: [
                ...group.conditions,
                {
                  id: `condition-${Date.now()}`,
                  questionId: '',
                  operator: 'equals',
                  value: '',
                },
              ],
            }
          : group
      )
    );
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    setRuleGroups(
      ruleGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.filter((c) => c.id !== conditionId),
            }
          : group
      )
    );
  };

  const updateCondition = (
    groupId: string,
    conditionId: string,
    updates: Partial<LogicCondition>
  ) => {
    setRuleGroups(
      ruleGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.map((c) =>
                c.id === conditionId ? { ...c, ...updates } : c
              ),
            }
          : group
      )
    );
  };

  const addRuleGroup = () => {
    setRuleGroups([
      ...ruleGroups,
      {
        id: `group-${Date.now()}`,
        operator: 'AND',
        conditions: [
          {
            id: `condition-${Date.now()}`,
            questionId: '',
            operator: 'equals',
            value: '',
          },
        ],
      },
    ]);
  };

  const removeRuleGroup = (groupId: string) => {
    if (ruleGroups.length > 1) {
      setRuleGroups(ruleGroups.filter((g) => g.id !== groupId));
    }
  };

  const updateGroupOperator = (groupId: string, operator: 'AND' | 'OR') => {
    setRuleGroups(
      ruleGroups.map((group) =>
        group.id === groupId ? { ...group, operator } : group
      )
    );
  };

  const addAction = () => {
    setActions([
      ...actions,
      { type: 'show', targetQuestionId: currentQuestion.id },
    ]);
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const updateAction = (index: number, updates: Partial<LogicAction>) => {
    setActions(actions.map((a, i) => (i === index ? { ...a, ...updates } : a)));
  };

  const getQuestionOptions = (questionId: string) => {
    const question = availableQuestions.find((q) => q.id === questionId);
    return question?.options || [];
  };

  const operatorNeedsValue = (operator: LogicOperator): boolean => {
    return operator !== 'is_empty' && operator !== 'is_not_empty';
  };

  const handleSave = () => {
    // Auto-generate name if somehow empty
    const finalRuleName = ruleName.trim() || generateRuleName();

    for (const group of ruleGroups) {
      for (const condition of group.conditions) {
        if (!condition.questionId) {
          alert('Please select a question for all conditions');
          return;
        }
        if (operatorNeedsValue(condition.operator) && !condition.value) {
          alert('Please enter a value for all conditions');
          return;
        }
      }
    }

    for (const action of actions) {
      if (!action.targetQuestionId && action.type !== 'jump_to') {
        alert('Please select a target question for all actions');
        return;
      }
    }

    const newRule: LogicRule = {
      id: rule?.id || `rule-${Date.now()}`,
      name: finalRuleName,
      enabled: true,
      ruleGroups,
      groupOperator,
      actions,
      createdAt: rule?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    onSave(newRule);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 z-[9999] overflow-y-auto flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full shadow-2xl flex flex-col max-h-[85vh] relative z-[10000]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {rule ? 'Edit' : 'Create'} Logic Rule
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Define conditions and actions for this question
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-700 hover:bg-white rounded-lg p-2 transition-all"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Auto-generated rule name - hidden from UI */}
          {/* Rule names are now auto-generated: {ruleName} */}

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">Conditions (IF)</h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                Show this question when...
              </span>
            </div>

            <div className="space-y-3">
              {ruleGroups.map((group, groupIndex) => (
                <div key={group.id} className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-600 bg-white px-2 py-1 rounded">GROUP {groupIndex + 1}</span>
                      <select
                        value={group.operator}
                        onChange={(e) => updateGroupOperator(group.id, e.target.value as 'AND' | 'OR')}
                        className="text-xs font-semibold px-3 py-1 border border-gray-300 rounded bg-white"
                      >
                        <option value="AND">ALL of these</option>
                        <option value="OR">ANY of these</option>
                      </select>
                    </div>
                    {ruleGroups.length > 1 && (
                      <button
                        onClick={() => removeRuleGroup(group.id)}
                        className="text-red-500 hover:text-red-700 text-xs font-semibold"
                      >
                        × Remove Group
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {group.conditions.map((condition) => (
                      <div key={condition.id} className="flex items-center gap-2">
                        <select
                          value={condition.questionId}
                          onChange={(e) =>
                            updateCondition(group.id, condition.id, { questionId: e.target.value })
                          }
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                        >
                          <option value="">Select question...</option>
                          {availableQuestions.map((q) => (
                            <option key={q.id} value={q.id}>
                              {q.title}
                            </option>
                          ))}
                        </select>

                        <select
                          value={condition.operator}
                          onChange={(e) =>
                            updateCondition(group.id, condition.id, { operator: e.target.value as LogicOperator })
                          }
                          className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                        >
                          {operatorOptions.map((op) => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </select>

                        {operatorNeedsValue(condition.operator) && (
                          <>
                            {condition.questionId && getQuestionOptions(condition.questionId).length > 0 ? (
                              <select
                                value={condition.value as string}
                                onChange={(e) =>
                                  updateCondition(group.id, condition.id, { value: e.target.value })
                                }
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                              >
                                <option value="">Select option...</option>
                                {getQuestionOptions(condition.questionId).map((opt) => (
                                  <option key={opt.id} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={condition.value as string}
                                onChange={(e) =>
                                  updateCondition(group.id, condition.id, { value: e.target.value })
                                }
                                placeholder="Enter value..."
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                            )}
                          </>
                        )}

                        {group.conditions.length > 1 && (
                          <button
                            onClick={() => removeCondition(group.id, condition.id)}
                            className="text-red-500 hover:text-red-700 p-1.5"
                            title="Remove"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => addCondition(group.id)}
                    className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-semibold flex items-center gap-1"
                  >
                    + Add Condition
                  </button>
                </div>
              ))}

              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={addRuleGroup}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold"
                >
                  + Add Group
                </button>
                {ruleGroups.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Combine groups with:</span>
                    <select
                      value={groupOperator}
                      onChange={(e) => setGroupOperator(e.target.value as 'AND' | 'OR')}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm font-semibold"
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-3">Actions (THEN)</h3>

            <div className="space-y-2">
              {actions.map((action, index) => (
                <div key={index} className="flex items-center gap-2 bg-green-50 rounded-lg p-3 border-2 border-green-200">
                  <select
                    value={action.type}
                    onChange={(e) => updateAction(index, { type: e.target.value as any })}
                    className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white font-semibold"
                  >
                    <option value="show">Show</option>
                    <option value="hide">Hide</option>
                    <option value="require">Require</option>
                    <option value="optional">Optional</option>
                    <option value="jump_to">Jump To</option>
                  </select>

                  <select
                    value={action.targetQuestionId || ''}
                    onChange={(e) => updateAction(index, { targetQuestionId: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">Select question...</option>
                    {action.type === 'show' || action.type === 'hide' || action.type === 'require' || action.type === 'optional' ? (
                      <option value={currentQuestion.id}>{currentQuestion.title} (this question)</option>
                    ) : (
                      allQuestions.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.title}
                        </option>
                      ))
                    )}
                  </select>

                  {actions.length > 1 && (
                    <button
                      onClick={() => removeAction(index)}
                      className="text-red-500 hover:text-red-700 p-1.5"
                      title="Remove"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={addAction}
              className="mt-3 text-green-600 hover:text-green-700 text-sm font-semibold flex items-center gap-1"
            >
              + Add Action
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t-2 border-gray-200 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-600 flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Remember to click <strong>"Save Changes"</strong> on the question after adding this rule</span>
          </p>

          <div className="flex gap-3 ml-4">
            <button
              onClick={onCancel}
              className="px-5 py-2.5 text-sm text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-100 font-semibold transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2.5 text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-bold shadow-lg hover:shadow-xl flex items-center gap-2 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {rule ? 'Update Rule' : 'Add Rule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleBuilder;
